import React, { useState, useRef } from 'react';
import {
  Box, Typography, Stack, Paper, Card, CardContent, CardActions,
  Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Snackbar, CircularProgress, Divider,
  Stepper, Step, StepLabel, Tooltip, LinearProgress, TextField,
  Table, TableHead, TableRow, TableCell, IconButton,
  Grid, Checkbox, FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import DrawIcon from '@mui/icons-material/Draw';
import LockIcon from '@mui/icons-material/Lock';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendIcon from '@mui/icons-material/Send';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PrintIcon from '@mui/icons-material/Print';
import ContractPrintView from '../components/ContractPrintView';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_CONTRACTS_QUERY,
  RESPOND_TO_CONTRACT_MUTATION,
  USER_SIGN_CONTRACT_MUTATION,
  ACCEPT_CONTRACT_MUTATION,
  CONTRACT_SERVICE_ITEMS_QUERY,
  WEDDING_SERVICES_QUERY,
  ADD_CONTRACT_SERVICE_ITEM_MUTATION,
  EDIT_CONTRACT_AS_PARTY_MUTATION,
  REMOVE_CONTRACT_SERVICE_ITEM_MUTATION,
  UPDATE_CONTRACT_SERVICE_ITEM_MUTATION,
  LEGAL_DOCUMENT_QUERY,
} from '../graphql/queries';
import MarkdownView from '../components/MarkdownView';
import SignaturePad from '../components/SignaturePad';
import { useAuth } from '../context/AuthContext';
import ContractItemDialog, { needsPrice } from '../components/ContractItemDialog';
import { fmtDate } from '../utils/dateUtils';

import Paged from '../components/Paged'; // lists beyond 10 are paged
// -- Status config --
const STATUS_CONFIG = {
  draft:                    { label: 'Draft',                color: 'default',  step: 0 },
  sent_for_review:          { label: 'Awaiting Your Review', color: 'warning',  step: 1 },
  user_requested_changes:   { label: 'Changes Requested',    color: 'info',     step: 1 },
  user_agreed:              { label: 'Agreed — Signing',     color: 'primary',  step: 2 },
  signed:                   { label: 'Fully Signed',         color: 'success',  step: 3 },
  active:                   { label: 'Active',               color: 'success',  step: 3 },
  terminated:               { label: 'Terminated',           color: 'error',    step: -1 },
};

const WORKFLOW_STEPS = ['Contract Created', 'Review & Agreement', 'E-Signature', 'Finalized'];

// -- Signature status badge --
// ── Service Items panel ───────────────────────────────────────────────────────
// The contract's party changes its lines while it can still be changed: a vendor sets quantity,
// unit price, discount and tax on its own service contract until signing it; a family adds
// services at their catalog price and changes quantities (prices are set by the admin/vendor).
function ServiceItemsPanel({ contract }) {
  const { user } = useAuth();
  const contractId = contract.id;
  const isVendor   = user?.role?.name === 'vendor';
  const signed     = Boolean(contract.userSignatureData || contract.signatureData);
  // The party or the family's matchmaker, until the contract is fully signed (the server decides)
  const canModify  = Boolean(contract.canEdit);
  const canPrice = canModify && isVendor;
  // A change now sends the contract back to the admin, and it is agreed and signed again
  const changeResets = canModify && (contract.status === 'user_agreed' || signed);

  const { data, loading, refetch } = useQuery(CONTRACT_SERVICE_ITEMS_QUERY, {
    variables: { contractId },
    fetchPolicy: 'network-only',
  });
  const { data: svcData } = useQuery(WEDDING_SERVICES_QUERY, {
    variables: { activeOnly: true },
    fetchPolicy: 'network-only',
    skip: !canModify,
  });

  const [addItem, { loading: adding }]      = useMutation(ADD_CONTRACT_SERVICE_ITEM_MUTATION,    { onCompleted: refetch });
  const [updateItem, { loading: updating }] = useMutation(UPDATE_CONTRACT_SERVICE_ITEM_MUTATION, { onCompleted: refetch });
  const [removeItem] = useMutation(REMOVE_CONTRACT_SERVICE_ITEM_MUTATION, { onCompleted: refetch });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [itemError, setItemError]   = useState('');

  const services = svcData?.weddingServices || [];
  const items    = data?.contractServiceItems || [];

  const gross = items.reduce((s, i) => s + Number(i.subtotal),      0);
  const disc  = items.reduce((s, i) => s + Number(i.discountAmount), 0);
  const tax   = items.reduce((s, i) => s + Number(i.taxAmount),      0);
  const net   = items.reduce((s, i) => s + Number(i.lineTotal),      0);
  const fmt   = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const saveItem = async (values) => {
    setItemError('');
    try {
      if (editing) await updateItem({ variables: { input: { id: editing.id, ...values } } });
      else await addItem({ variables: { input: { contractId, ...values, addedBy: 'user' } } });
      setPickerOpen(false);
      setEditing(null);
    } catch (e) { setItemError(e.message); }
  };

  if (loading) return <CircularProgress size={18} sx={{ display: 'block', mx: 'auto', mt: 1 }} />;

  return (
    <Box mt={1.5}>
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ShoppingCartIcon fontSize="small" /> Services &amp; Pricing
        </Typography>
        {canModify && (
          <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => { setItemError(''); setPickerOpen(true); }}>
            Add Service
          </Button>
        )}
      </Stack>
      {changeResets && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Changing the services now sends the contract back to the admin for review. The agreement and any signatures are cleared, and it is agreed and signed again.
        </Alert>
      )}
      {canPrice && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Set the quantity, unit price, discount and tax for each service before you sign; the admin reviews them when countersigning.
        </Typography>
      )}

      {canPrice && items.some(needsPrice) && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Set the quantity, unit price, discount and tax for {items.filter(needsPrice).length} service(s) before you sign; they are required.
        </Alert>
      )}

      {items.length === 0 && canModify && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No services added yet. Click "Add Service" to add services from the catalog.
        </Alert>
      )}

      {items.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Discount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Tax</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                {canModify && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover sx={{ opacity: item.isApproved ? 1 : 0.8 }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.serviceName}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.serviceCategory} · {item.unit}</Typography>
                    {!item.isApproved && (
                      <Chip label={item.addedBy === 'user' ? 'Pending Approval' : 'Admin Added'} size="small"
                        color={item.addedBy === 'user' ? 'warning' : 'default'} sx={{ ml: 1 }} />
                    )}
                    {canPrice && needsPrice(item) && <Chip label="Price needed" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">{Number(item.quantity)}</TableCell>
                  <TableCell align="right">{fmt(item.unitPrice)}</TableCell>
                  <TableCell align="right">
                    {Number(item.discountAmount) > 0
                      ? <Typography variant="body2" color="success.main">
                          -{fmt(item.discountAmount)}
                          <Typography component="span" variant="caption" color="text.secondary" display="block">
                            {item.discountType === 'percent' ? `${Number(item.discountValue)}%` : 'flat'}
                          </Typography>
                        </Typography>
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {fmt(item.taxAmount)}
                    <Typography variant="caption" color="text.secondary" display="block">{Number(item.taxRate)}%</Typography>
                  </TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={700}>{fmt(item.lineTotal)}</Typography></TableCell>
                  {canModify && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={canPrice ? 'Edit quantity, price, discount and tax' : 'Change quantity'}>
                        <IconButton size="small" onClick={() => { setItemError(''); setEditing(item); }}>
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(isVendor || (item.addedBy === 'user' && !item.isApproved)) && (
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error"
                            onClick={() => removeItem({ variables: { id: item.id } })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Paper elevation={0} sx={{ p: 1.5, mt: 1, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
            <Grid container spacing={1}>
              <Grid item xs={3}><Typography variant="caption" color="text.secondary">Subtotal</Typography><Typography variant="body2">{fmt(gross)}</Typography></Grid>
              <Grid item xs={3}><Typography variant="caption" color="text.secondary">Discount</Typography><Typography variant="body2" color="success.main">-{fmt(disc)}</Typography></Grid>
              <Grid item xs={3}><Typography variant="caption" color="text.secondary">GST / Tax</Typography><Typography variant="body2" color="warning.main">+{fmt(tax)}</Typography></Grid>
              <Grid item xs={3}><Typography variant="caption" fontWeight={700}>Net Payable</Typography><Typography variant="h6" fontWeight={800} color="primary.main">{fmt(net)}</Typography></Grid>
            </Grid>
          </Paper>
        </>
      )}

      <ContractItemDialog
        open={pickerOpen || Boolean(editing)}
        item={editing}
        services={services}
        canPrice={canPrice}
        saving={adding || updating}
        error={itemError}
        onClose={() => { setPickerOpen(false); setEditing(null); }}
        onSave={saveItem}
      />
    </Box>
  );
}


// ── Payment Milestones panel (read-only for client) ───────────────────────────
function MilestonesPanel({ contract }) {
  const milestones  = contract.milestones || [];
  const totalAmount = milestones.reduce((s, m) => s + Number(m.amount), 0);
  const paidAmount  = milestones.filter(m => m.paidAt).reduce((s, m) => s + Number(m.amount), 0);
  const paidPct     = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

  if (milestones.length === 0) return null;

  return (
    <Box mt={2}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" fontWeight={700} mb={1}>Payment Milestones</Typography>
      <Box mb={1.5}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Paid: ₹{paidAmount.toLocaleString('en-IN')} / ₹{totalAmount.toLocaleString('en-IN')}
          </Typography>
          <Typography variant="caption" fontWeight={600}>{paidPct}%</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={paidPct}
          color={paidPct === 100 ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
      </Box>
      <Table size="small" sx={{ mb: 1 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 700, py: 0.5, width: 32 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Stage Name</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5 }} align="right">Amount</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Due Date</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Paid At</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {milestones.map((m, idx) => (
            <TableRow key={m.id} sx={{ bgcolor: m.paidAt ? '#f1f8e9' : 'transparent' }}>
              <TableCell sx={{ py: 0.5, color: 'text.secondary' }}>{idx + 1}</TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Typography variant="body2">{m.stageName}</Typography>
                {m.notes && <Typography variant="caption" color="text.secondary" display="block">{m.notes}</Typography>}
              </TableCell>
              <TableCell sx={{ py: 0.5 }} align="right">
                <Typography variant="body2" fontWeight={600}>{' '}{Number(m.amount).toLocaleString('en-IN')}</Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Typography variant="caption">{m.dueDate ? fmtDate(m.dueDate) : '\u2014'}</Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Typography variant="caption" color={m.paidAt ? 'success.main' : 'text.disabled'}>
                  {m.paidAt ? fmtDate(m.paidAt) : '\u2014'}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                {m.paidAt
                  ? <Chip label="Paid" color="success" size="small" icon={<CheckCircleIcon />} />
                  : <Chip label="Pending" color="default" size="small" />}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function SignatureBadge({ label, signed, signedAt }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {signed
        ? <CheckCircleIcon color="success" fontSize="small" />
        : <PendingIcon color="disabled" fontSize="small" />}
      <Box>
        <Typography variant="caption" fontWeight={600}>{label}</Typography>
        {signed && signedAt
          ? <Typography variant="caption" display="block" color="text.secondary">{fmtDate(signedAt, 'datetime')}</Typography>
          : <Typography variant="caption" display="block" color="text.secondary">Pending</Typography>
        }
      </Box>
    </Stack>
  );
}

// -- Contract detail / sign dialog --
// The contract's key points (Admin > Legal & Policies), with the box to tick before agreeing
function ContractKeyTerms({ agreed, onChange }) {
  const { data } = useQuery(LEGAL_DOCUMENT_QUERY, { variables: { key: 'contract_key_terms' } });
  const doc = data?.legalDocument;
  if (!doc) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fffde7', borderColor: '#f9a825' }}>
      <Typography variant="subtitle2" fontWeight={700} mb={1}>{doc.title}</Typography>
      <MarkdownView text={doc.body} dense />
      <FormControlLabel
        sx={{ alignItems: 'flex-start', mt: 0.5 }}
        control={<Checkbox checked={agreed} onChange={(e) => onChange(e.target.checked)} sx={{ pt: 0.25 }} />}
        label={<Typography variant="body2">I have read this contract and these key points, and I agree to the <a href="/legal/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a> and <a href="/legal/refunds" target="_blank" rel="noopener noreferrer">Refunds &amp; Cancellations Policy</a>.</Typography>}
      />
    </Paper>
  );
}

// The party or the family's matchmaker edits the contract's title, text, terms and due date
function ContractEditForm({ contract, form, setForm }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const agreedOrSigned = contract.status === 'user_agreed' || contract.signatureData || contract.userSignatureData;
  return (
    <Box mt={2}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary.dark">Edit contract</Typography>
      <Alert severity={agreedOrSigned ? 'warning' : 'info'} sx={{ mb: 2 }}>
        Your changes go to the admin, who reviews them and sends the contract again.
        {agreedOrSigned ? ' The agreement and the signatures so far are cleared: it is agreed and signed again.' : ''}
        {' '}Services and quantities are changed in Services &amp; Pricing.
      </Alert>
      <Stack spacing={2}>
        <TextField label="Title" value={form.title} onChange={set('title')} fullWidth size="small" required />
        <TextField label="Contract text" value={form.body} onChange={set('body')} fullWidth multiline minRows={6} size="small" />
        <TextField label="Terms & conditions" value={form.terms} onChange={set('terms')} fullWidth multiline minRows={3} size="small" />
        <TextField label="Due date" type="date" value={form.dueDate} onChange={set('dueDate')} size="small" InputLabelProps={{ shrink: true }} sx={{ maxWidth: 220 }} />
        <TextField label="What did you change, and why? *" value={form.changeNote} onChange={set('changeNote')} fullWidth multiline minRows={2} size="small"
          placeholder="e.g. Added 2 more hall hours for the reception; moved the due date to 15 January" />
      </Stack>
    </Box>
  );
}

function ContractDetailDialog({ open, contract, onClose, onRespond, onSign, onAccept, onEdit }) {
  const { user } = useAuth();
  const [changeNotes, setChangeNotes] = useState('');
  const [editForm, setEditForm]       = useState(null);
  const [canvasData, setCanvasData]   = useState(null);
  const [view, setView]               = useState('detail'); // 'detail' | 'request_changes' | 'sign' | 'edit'
  // The party confirms they read the contract's key points before agreeing / signing / accepting
  const [keyAgreed, setKeyAgreed]     = useState(false);
  const [printOpen, setPrintOpen]     = useState(false);
  const sigPadRef                     = useRef(null);
  const statusCfg = STATUS_CONFIG[contract?.status] || STATUS_CONFIG.draft;

  const startEdit = () => {
    setEditForm({
      title: contract.title || '', body: contract.body || '', terms: contract.terms || '',
      dueDate: contract.dueDate ? String(contract.dueDate).slice(0, 10) : '', changeNote: '',
    });
    setView('edit');
  };
  const handleClose = () => {
    setView('detail');
    setEditForm(null);
    setKeyAgreed(false);
    setChangeNotes('');
    setCanvasData(null);
    setPrintOpen(false);
    sigPadRef.current?.clear();
    onClose();
  };

  if (!contract) return null;

  // A matchmaker, or the groom of a joint contract, sees it read-only; the assigned party acts on it
  const isParty       = contract.assignedToUser === user?.id;
  const canRespond    = isParty && contract.status === 'sent_for_review';
  const canSign       = isParty && contract.status === 'user_agreed' && !contract.userSignatureData;
  const alreadySigned = Boolean(contract.userSignatureData);
  const partyName     = contract.parties?.name || (contract.assignedUser?.profile
    ? `${contract.assignedUser.profile.firstName} ${contract.assignedUser.profile.lastName}` : contract.assignedUser?.email);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>{contract.title}</Typography>
            {contract.refNumber && (
              <Typography variant="caption" color="text.secondary">Ref: {contract.refNumber}</Typography>
            )}
            {contract.parties?.label && <Typography variant="body2" color="text.secondary">{contract.parties.label}</Typography>}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
            <Tooltip title="Print / Save as PDF">
              <IconButton size="small" onClick={() => setPrintOpen(true)}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {/* Stepper */}
        {contract.status !== 'terminated' && (
          <Stepper activeStep={statusCfg.step} sx={{ mb: 3 }}>
            {WORKFLOW_STEPS.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
          </Stepper>
        )}

        {/* Status alerts */}
        {!isParty && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {contract.canEdit
              ? `You can edit this contract for ${partyName || 'the family'} until it is signed; they review, agree and sign it.`
              : `View only: this contract is for ${partyName || 'the family'}, who reviews and signs it.`}
          </Alert>
        )}
        {isParty && contract.status === 'sent_for_review' && (
          <Alert severity="info" icon={<PendingIcon />} sx={{ mb: 2 }}>
            The admin has sent this contract for your review. Please read carefully and either agree or request changes.
          </Alert>
        )}
        {contract.status === 'user_requested_changes' && (
          <Alert severity="info" icon={<HourglassEmptyIcon />} sx={{ mb: 2 }}>
            Changes were requested or made. The admin is reviewing them and will send the contract again.
          </Alert>
        )}
        {isParty && contract.status === 'user_agreed' && !alreadySigned && (
          <Alert severity="success" icon={<DrawIcon />} sx={{ mb: 2 }}>
            You've agreed to this contract. Please draw your e-signature to finalize your part.
          </Alert>
        )}
        {contract.status === 'signed' && contract.acceptedAt && (
          <Alert severity="success" icon={<LockIcon />} sx={{ mb: 2 }}>
            This contract is fully signed, approved and accepted on {new Date(contract.acceptedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.
            Both parties are bound by its terms.
          </Alert>
        )}
        {/* Final check: the party accepts the approved contract; only then vendors open / offerings go live */}
        {contract.status === 'signed' && !contract.acceptedAt && (
          <Alert
            severity={isParty ? 'warning' : 'info'}
            icon={<TaskAltIcon />}
            sx={{ mb: 2 }}
            action={isParty && (
              <Button color="inherit" variant="outlined" size="small" disabled={!keyAgreed} onClick={() => onAccept(contract.id)}>
                Accept contract
              </Button>
            )}
          >
            {isParty
              ? (contract.assignedUser?.role?.name === 'vendor' || user?.role?.name === 'vendor'
                ? 'Signed by both sides and approved by the admin. Accept it to make your offerings for these services live.'
                : 'Signed by both sides and approved by the admin. Accept it to open the vendors for your contracted services.')
              : `Signed and approved — waiting for ${partyName} to accept it.`}
          </Alert>
        )}

        {/* Change request notes */}
        {contract.reviewNotes && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
            <Typography variant="caption" fontWeight={700} color="info.dark">CHANGES REQUESTED / MADE:</Typography>
            <Typography variant="body2" mt={0.5}>{contract.reviewNotes}</Typography>
          </Paper>
        )}

        {/* Meta chips */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={2} flexWrap="wrap">
          {contract.cost && <Chip label={` ${Number(contract.cost).toLocaleString('en-IN')}`} variant="outlined" size="small" />}
          {contract.dueDate && <Chip label={`Due: ${fmtDate(contract.dueDate)}`} variant="outlined" size="small" color="warning" />}
          {contract.template && <Chip label={`Template: ${contract.template.name}`} variant="outlined" size="small" />}
          {contract.creator && <Chip label={`From: ${contract.creator.email}`} variant="outlined" size="small" />}
        </Stack>

        {/* Body / Terms */}
        {contract.body && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Contract Body</Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{contract.body}</Typography>
            </Paper>
          </Box>
        )}
        {contract.terms && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Terms & Conditions</Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 150, overflow: 'auto', bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{contract.terms}</Typography>
            </Paper>
          </Box>
        )}
        {contract.penaltyClause && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff3e0', borderColor: 'warning.light' }}>
            <Typography variant="caption" fontWeight={700} color="warning.dark">PENALTY CLAUSE</Typography>
            <Typography variant="body2" mt={0.5}>{contract.penaltyClause}</Typography>
          </Paper>
        )}

        {/* Dual-signature status */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>E-Signature Status</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <SignatureBadge label="Admin (Creator)" signed={Boolean(contract.signatureData)}  signedAt={contract.signedAt} />
          <SignatureBadge label="You (Assignee)"  signed={Boolean(contract.userSignatureData)} signedAt={contract.userSignedAt} />
        </Stack>

        {/* Signed - show signature previews */}
        {contract.status === 'signed' && contract.userSignatureData && contract.userSignatureData.startsWith('data:') && (
          <Box mt={2}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">YOUR SIGNATURE</Typography>
            <Box sx={{ mt: 0.5, p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa', display: 'inline-block' }}>
              <img src={contract.userSignatureData} alt="Your signature" style={{ maxHeight: 80, display: 'block' }} />
            </Box>
          </Box>
        )}

        {/* Request changes */}
        {view === 'request_changes' && (
          <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="info.dark">Request Changes</Typography>
            <TextField
              label="Describe the changes you need"
              value={changeNotes}
              onChange={e => setChangeNotes(e.target.value)}
              fullWidth multiline rows={4} size="small"
              placeholder="e.g. Please update the cost to ₹50,000 and extend the due date by 2 weeks..."
            />
          </Box>
        )}

        {view === 'edit' && editForm && <ContractEditForm contract={contract} form={editForm} setForm={setEditForm} />}

        {/* Canvas signature */}
        {view === 'sign' && (
          <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="warning" sx={{ mb: 2 }}>
              By signing, you legally agree to all the terms of this contract. This action cannot be undone.
            </Alert>
            <SignaturePad ref={sigPadRef} onChange={setCanvasData} />
          </Box>
        )}

        {/* Before agreeing, signing or accepting: the key points in plain language */}
        {isParty && (canRespond || canSign || (contract.status === 'signed' && !contract.acceptedAt)) && (
          <ContractKeyTerms agreed={keyAgreed} onChange={setKeyAgreed} />
        )}

        {/* Services & Pricing */}
        {view === 'detail' && (
          <ServiceItemsPanel contract={contract} />
        )}

        {/* Payment Milestones */}
        {view === 'detail' && (
          <MilestonesPanel contract={contract} />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleClose}>Close</Button>

        {view === 'detail' && contract.canEdit && (
          <Button variant="outlined" startIcon={<EditNoteIcon />} onClick={startEdit}>Edit contract</Button>
        )}
        {view === 'edit' && (
          <>
            <Button onClick={() => { setView('detail'); setEditForm(null); }}>Back</Button>
            <Button variant="contained" startIcon={<SendIcon />} disabled={!editForm?.title.trim() || !editForm?.changeNote.trim()}
              onClick={async () => { if (await onEdit(contract, editForm)) handleClose(); }}>
              Save and send to admin
            </Button>
          </>
        )}

        {view === 'detail' && canRespond && (
          <>
            <Button variant="outlined" color="info" startIcon={<EditNoteIcon />}
              onClick={() => setView('request_changes')}>
              Request Changes
            </Button>
            <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={!keyAgreed}
              onClick={() => { onRespond({ id: contract.id, response: 'agree' }); handleClose(); }}>
              Agree to Contract
            </Button>
          </>
        )}

        {view === 'request_changes' && (
          <>
            <Button onClick={() => { setView('detail'); setChangeNotes(''); }}>Back</Button>
            <Button variant="contained" color="info" startIcon={<SendIcon />}
              disabled={!changeNotes.trim()}
              onClick={() => { onRespond({ id: contract.id, response: 'request_changes', notes: changeNotes }); handleClose(); }}>
              Send Change Request
            </Button>
          </>
        )}

        {view === 'detail' && canSign && (
          <Button variant="contained" color="primary" startIcon={<DrawIcon />} disabled={!keyAgreed}
            onClick={() => setView('sign')}>
            Sign Contract
          </Button>
        )}

        {view === 'sign' && (
          <>
            <Button onClick={() => { setView('detail'); setCanvasData(null); sigPadRef.current?.clear(); }}>Back</Button>
            <Button variant="contained" color="success" startIcon={<LockIcon />}
              disabled={!canvasData}
              onClick={() => { onSign({ id: contract.id, signatureData: canvasData }); handleClose(); }}>
              Confirm & Sign
            </Button>
          </>
        )}
      </DialogActions>

      {/* Print/Preview dialog */}
      {printOpen && contract && (
        <ContractPrintView
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          contract={contract}
        />
      )}
    </Dialog>
  );
}

// -- Main page --
export default function ContractsPage() {
  const { user } = useAuth();
  const [viewContract, setViewContract] = useState(null);
  const [snack, setSnack]               = useState({ open: false, msg: '', severity: 'success' });

  const { data, loading, error, refetch } = useQuery(MY_CONTRACTS_QUERY);
  const contracts = data?.myContracts || [];

  const [respondToContract] = useMutation(RESPOND_TO_CONTRACT_MUTATION);
  const [userSignContract]  = useMutation(USER_SIGN_CONTRACT_MUTATION);
  const [acceptContract]    = useMutation(ACCEPT_CONTRACT_MUTATION);
  const [editContract]      = useMutation(EDIT_CONTRACT_AS_PARTY_MUTATION);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const handleRespond = async ({ id, response, notes }) => {
    try {
      await respondToContract({ variables: { id, response, notes } });
      await refetch();
      showSnack(response === 'agree' ? `You've agreed to the contract! Please proceed to sign.` : 'Change request sent to admin.');
    } catch (e) { showSnack(e.message, 'error'); }
  };

  // Only the fields that changed are sent; returns true when saved
  const handleEdit = async (contract, form) => {
    const was = { title: contract.title || '', body: contract.body || '', terms: contract.terms || '', dueDate: contract.dueDate ? String(contract.dueDate).slice(0, 10) : '' };
    const input = { changeNote: form.changeNote };
    Object.keys(was).forEach(k => { if (form[k] !== was[k]) input[k] = form[k]; });
    try {
      await editContract({ variables: { id: contract.id, input } });
      await refetch();
      showSnack('Changes saved and sent to the admin for review. The contract is then agreed and signed again.');
      return true;
    } catch (e) { showSnack(e.message, 'error'); return false; }
  };

  const handleSign = async ({ id, signatureData }) => {
    try {
      await userSignContract({ variables: { id, signatureData } });
      await refetch();
      showSnack('Contract signed! Once the admin approves it, accept it here to finish.');
    } catch (e) { showSnack(e.message, 'error'); }
  };

  const handleAccept = async (id) => {
    try {
      const { data: res } = await acceptContract({ variables: { id } });
      await refetch();
      setViewContract(v => (v && v.id === id ? { ...v, acceptedAt: res.acceptContract.acceptedAt } : v));
      showSnack(user?.role?.name === 'vendor'
        ? 'Contract accepted — your offerings for these services are now live.'
        : 'Contract accepted — your vendors are now open in Service Providers.');
    } catch (e) { showSnack(e.message, 'error'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 3 }}>{error.message}</Alert>;

  const pending   = contracts.filter(c => ['sent_for_review', 'user_agreed', 'active', 'draft'].includes(c.status));
  const inChanges = contracts.filter(c => c.status === 'user_requested_changes');
  const completed = contracts.filter(c => c.status === 'signed');
  const other     = contracts.filter(c => !['sent_for_review', 'user_agreed', 'user_requested_changes', 'signed', 'active', 'draft'].includes(c.status));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>My Contracts</Typography>
          <Typography variant="body2" color="text.secondary">
            Review, agree and e-sign formal agreements with the admin
          </Typography>
        </Box>
      </Stack>

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <LockIcon color="success" />
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            All contracts are stored securely with full audit trail, version control, and dual-party e-signature enforcement.
            Neither party is bound until both have signed.
          </Typography>
        </Stack>
      </Paper>

      {pending.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="warning.dark">Action Required ({pending.length})</Typography>
          <Stack spacing={2}><Paged items={pending}>{rows => rows.map(c => <ContractCard key={c.id} contract={c} onClick={() => setViewContract(c)} />)}</Paged></Stack>
        </Box>
      )}
      {inChanges.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="info.dark">Changes Requested — Awaiting Admin ({inChanges.length})</Typography>
          <Stack spacing={2}><Paged items={inChanges}>{rows => rows.map(c => <ContractCard key={c.id} contract={c} onClick={() => setViewContract(c)} />)}</Paged></Stack>
        </Box>
      )}
      {completed.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="success.dark">Signed & Finalized ({completed.length})</Typography>
          <Stack spacing={2}><Paged items={completed}>{rows => rows.map(c => <ContractCard key={c.id} contract={c} onClick={() => setViewContract(c)} />)}</Paged></Stack>
        </Box>
      )}
      {other.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="text.secondary">Other</Typography>
          <Stack spacing={2}><Paged items={other}>{rows => rows.map(c => <ContractCard key={c.id} contract={c} onClick={() => setViewContract(c)} />)}</Paged></Stack>
        </Box>
      )}

      {contracts.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px dashed #e0e0e0' }}>
          <DescriptionIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
          <Typography color="text.secondary">No contracts assigned to you yet.</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Contracts created by the admin and assigned to you will appear here.
          </Typography>
        </Paper>
      )}

      <ContractDetailDialog
        open={Boolean(viewContract)}
        contract={viewContract}
        onClose={() => setViewContract(null)}
        onRespond={handleRespond}
        onSign={handleSign}
        onAccept={handleAccept}
        onEdit={handleEdit}
      />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

// -- Contract summary card --
function ContractCard({ contract, onClick }) {
  const { user } = useAuth();
  const isParty     = contract.assignedToUser === user?.id;
  const statusCfg   = STATUS_CONFIG[contract.status] || STATUS_CONFIG.draft;
  const userSigned  = Boolean(contract.userSignatureData);
  const adminSigned = Boolean(contract.signatureData);
  const signPct     = (contract.status === 'user_agreed' || contract.status === 'signed')
    ? ((adminSigned ? 50 : 0) + (userSigned ? 50 : 0)) : 0;

  return (
    <Card elevation={1} sx={{ borderRadius: 2, '&:hover': { boxShadow: 4, cursor: 'pointer' } }} onClick={onClick}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>{contract.title}</Typography>
            {contract.parties?.label && <Typography variant="body2" color="text.secondary">{contract.parties.label}</Typography>}
            <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
              <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
              {contract.cost && <Chip label={`₹${Number(contract.cost).toLocaleString('en-IN')}`} size="small" variant="outlined" />}
              {contract.dueDate && <Chip label={`Due: ${fmtDate(contract.dueDate)}`} size="small" variant="outlined" color="warning" />}
            </Stack>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5} ml={2}>
            <Tooltip title={adminSigned ? 'Admin signed' : 'Admin pending'}>
              <CheckCircleIcon color={adminSigned ? 'success' : 'disabled'} fontSize="small" />
            </Tooltip>
            <Tooltip title={userSigned ? 'You signed' : 'Your signature pending'}>
              <DrawIcon color={userSigned ? 'success' : 'disabled'} fontSize="small" />
            </Tooltip>
          </Stack>
        </Stack>

        {signPct > 0 && (
          <Box mt={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Signature progress</Typography>
              <Typography variant="caption" color="text.secondary">{signPct}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={signPct}
              color={signPct === 100 ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
          </Box>
        )}

        {contract.reviewNotes && (
          <Paper variant="outlined" sx={{ mt: 1.5, p: 1, bgcolor: '#e3f2fd', borderColor: 'info.light' }}>
            <Typography variant="caption" fontWeight={600} color="info.dark">Your change request: </Typography>
            <Typography variant="caption" color="text.secondary">
              {contract.reviewNotes.substring(0, 100)}{contract.reviewNotes.length > 100 ? '...' : ''}
            </Typography>
          </Paper>
        )}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" startIcon={<DescriptionIcon />}>View Contract</Button>
        {!isParty && <Chip label={`For ${contract.parties?.name || (contract.assignedUser?.profile ? `${contract.assignedUser.profile.firstName} ${contract.assignedUser.profile.lastName}` : contract.assignedUser?.email || 'family')} · view only`} size="small" variant="outlined" />}
        {isParty && contract.status === 'sent_for_review' && <Chip label="Review Needed" color="warning" size="small" icon={<PendingIcon />} />}
        {isParty && contract.status === 'user_agreed' && !userSigned && <Chip label="Sign Now" color="primary" size="small" icon={<DrawIcon />} />}
        {contract.status === 'signed' && contract.acceptedAt && <Chip label="Accepted" color="success" size="small" icon={<LockIcon />} />}
        {contract.status === 'signed' && !contract.acceptedAt && (
          <Chip label={isParty ? 'Accept Now' : 'Awaiting acceptance'} color="warning" size="small" icon={<TaskAltIcon />} />
        )}
      </CardActions>
    </Card>
  );
}